import type { ReactNode } from 'react';
import { theme } from '@agentisai/design-system';

export interface RailNewButtonProps {
  readonly onClick: () => void;
  readonly children: ReactNode;
  /** Accessible name, when it should differ from the visible label (e.g. a hint). */
  readonly 'aria-label'?: string;
  /** Native tooltip — e.g. a "coming soon" hint on an intentionally inert action. */
  readonly title?: string;
  /** Disable while an action it triggers is already in flight (Drop 8 upload). */
  readonly disabled?: boolean;
}

/**
 * The charcoal "+ New" pill that sits at the right of a `RailPanel` subheader —
 * the DS rail-panel fixture's `NewActionButton` (which the DS does not export from
 * its barrel). Shared by the Workstreams and Files rails so the pill's tokens live
 * in one place rather than being copy-pasted per rail.
 */
export function RailNewButton({
  onClick,
  children,
  'aria-label': ariaLabel,
  title,
  disabled = false,
}: RailNewButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      disabled={disabled}
      style={{
        background: theme.color.neutral.charcoal,
        color: theme.color.neutral.white,
        border: 0,
        padding: `${theme.space['0_5']} ${theme.space[2]}`,
        borderRadius: theme.radii.sm,
        fontFamily: theme.typography.fontFamily.sans,
        fontSize: theme.typography.fontSize.nano,
        fontWeight: theme.typography.fontWeight.semibold,
        cursor: disabled ? 'default' : 'pointer',
        lineHeight: 1.4,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}
RailNewButton.displayName = 'RailNewButton';
